import { z } from 'zod';

/**
 * Validaciones cliente espejo del contrato (§5bis.5/§5bis.6):
 *
 *  - `descripcion`: 1..2000, requerida.
 *  - `orden`: entero ≥ 0, requerido (único por ítem).
 *  - `tipoPlazo`: `Fijo` | `PorEstudio` | `SinCura`, requerido.
 *  - En `Fijo`: `plazoValor` (entero > 0) y `plazoUnidad` OBLIGATORIOS.
 *  - En `PorEstudio`/`SinCura`: `plazoValor`/`plazoUnidad` PROHIBIDOS.
 *  - `alertaPreviaValor`/`alertaPreviaUnidad`: opcionales (no contractual), pero
 *    ambos o ninguno — entero > 0 cuando se envían.
 *  - `notas`: ≤ 2000, opcional.
 *
 * `aplicaPlazoCura` NO se valida ni se envía: lo deriva el backend.
 */

const tipoPlazo = z.enum(['Fijo', 'PorEstudio', 'SinCura']);
const unidadPlazo = z.enum(['Minutos', 'Horas', 'DiasCalendario']);

const descripcionSupuesto = z
  .string()
  .trim()
  .min(1, 'La descripción es obligatoria.')
  .max(2000, 'Máximo 2000 caracteres.');

const ordenSupuesto = z.number().int('Debe ser un entero.').min(0, 'El orden debe ser ≥ 0.');
const plazoValor = z.number().int('Debe ser un entero.').positive('Debe ser mayor que 0.');
const alertaPreviaValor = z.number().int('Debe ser un entero.').positive('Debe ser mayor que 0.');
const notasSupuesto = z.string().trim().max(2000, 'Máximo 2000 caracteres.');

/**
 * Refinamiento compartido: el plazo debe ser coherente con el modo.
 * Espejo de `resolve-plazo.helper.ts` del backend; falla antes de pegar
 * a la red (evita un `400 SUPUESTO_PLAZO_INVALIDO`).
 */
function refinarPlazo(
  tipoPlazo: 'Fijo' | 'PorEstudio' | 'SinCura',
  plazoValor: number | null | undefined,
  plazoUnidad: 'Minutos' | 'Horas' | 'DiasCalendario' | null | undefined,
  ctx: z.RefinementCtx,
): void {
  if (tipoPlazo === 'Fijo') {
    if (plazoValor === undefined || plazoValor === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['plazoValor'],
        message: 'En plazo fijo el valor es obligatorio.',
      });
    }
    if (plazoUnidad === undefined || plazoUnidad === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['plazoUnidad'],
        message: 'En plazo fijo la unidad es obligatoria.',
      });
    }
    return;
  }
  // PorEstudio / SinCura: el plazo numérico no aplica.
  if (plazoValor !== undefined && plazoValor !== null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['plazoValor'],
      message: 'Este modo no admite un plazo numérico.',
    });
  }
  if (plazoUnidad !== undefined && plazoUnidad !== null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['plazoUnidad'],
      message: 'Este modo no admite una unidad de plazo.',
    });
  }
}

/**
 * El aviso previo es un par cantidad+unidad independiente del modo de plazo:
 * ambos o ninguno (espejo de `resolverAlerta` del backend).
 */
function refinarAviso(
  alertaPreviaValor: number | null | undefined,
  alertaPreviaUnidad: 'Minutos' | 'Horas' | 'DiasCalendario' | null | undefined,
  ctx: z.RefinementCtx,
): void {
  const tieneValor = alertaPreviaValor !== undefined && alertaPreviaValor !== null;
  const tieneUnidad = alertaPreviaUnidad !== undefined && alertaPreviaUnidad !== null;
  if (tieneValor === tieneUnidad) return;
  if (!tieneValor) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['alertaPreviaValor'],
      message: 'Indique la cantidad del aviso previo.',
    });
  }
  if (!tieneUnidad) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['alertaPreviaUnidad'],
      message: 'Seleccione la unidad del aviso previo.',
    });
  }
}

export const crearSupuestoSchema = z
  .object({
    descripcion: descripcionSupuesto,
    orden: ordenSupuesto,
    tipoPlazo,
    plazoValor: plazoValor.optional(),
    plazoUnidad: unidadPlazo.optional(),
    alertaPreviaValor: alertaPreviaValor.optional(),
    alertaPreviaUnidad: unidadPlazo.optional(),
    notas: notasSupuesto.optional(),
  })
  .superRefine((data, ctx) => {
    refinarPlazo(data.tipoPlazo, data.plazoValor, data.plazoUnidad, ctx);
    refinarAviso(data.alertaPreviaValor, data.alertaPreviaUnidad, ctx);
  });

/**
 * Update: todos opcionales y los campos de plazo aceptan `null` para
 * limpiarlos al pasar de `Fijo` a otro modo (§5bis.6). El refinamiento
 * solo corre cuando `tipoPlazo` viaja en el patch (si no, no hay
 * suficiente contexto: el backend mergea contra el estado actual).
 */
export const actualizarSupuestoSchema = z
  .object({
    descripcion: descripcionSupuesto.optional(),
    orden: ordenSupuesto.optional(),
    tipoPlazo: tipoPlazo.optional(),
    plazoValor: z.union([plazoValor, z.null()]).optional(),
    plazoUnidad: z.union([unidadPlazo, z.null()]).optional(),
    alertaPreviaValor: z.union([alertaPreviaValor, z.null()]).optional(),
    alertaPreviaUnidad: z.union([unidadPlazo, z.null()]).optional(),
    notas: z.union([notasSupuesto, z.null()]).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.tipoPlazo !== undefined) {
      refinarPlazo(data.tipoPlazo, data.plazoValor, data.plazoUnidad, ctx);
    }
    if (data.alertaPreviaValor !== undefined || data.alertaPreviaUnidad !== undefined) {
      refinarAviso(data.alertaPreviaValor, data.alertaPreviaUnidad, ctx);
    }
  });

export const filtrosSupuestoSchema = z.object({
  includeInactive: z.coerce.boolean().optional(),
});

export type CrearSupuestoInput = z.infer<typeof crearSupuestoSchema>;
export type ActualizarSupuestoInput = z.infer<typeof actualizarSupuestoSchema>;
export type FiltrosSupuestoInput = z.infer<typeof filtrosSupuestoSchema>;
